import { useState } from "react";
import { getPhotoUrl } from '@/utils/photoUrl';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Link2, UserCheck, Users, UserPlus, Mail, Lock, User, Search, Edit, Save } from "lucide-react";

const POSITIONS = ["Ponteiro", "Oposto", "Central", "Levantador", "Líbero"];

function getPlayerName(p) {
  return p?.first_name ? `${p.first_name} ${p.last_name}` : p?.name || '';
}

export function LinkPlayerDialog({ open, onOpenChange, evaluatorName, players, onLink, isLinking }) {
  const [playerId, setPlayerId] = useState("");
  const [password, setPassword] = useState("");

  const handleOpenChange = (v) => {
    if (!v) { setPlayerId(""); setPassword(""); }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" /> Vincular Avaliador a Jogador
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            Vincule <strong>{evaluatorName}</strong> a um jogador existente.
          </p>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Selecione o Jogador</Label>
            <Select value={playerId} onValueChange={setPlayerId}>
              <SelectTrigger className="bg-input/50"><SelectValue placeholder="Escolha um jogador" /></SelectTrigger>
              <SelectContent>
                {players.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{getPlayerName(p)} ({p.position})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Senha para Conta de Jogador</Label>
            <Input type="password" placeholder="Defina uma senha" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-input/50" />
            <p className="text-xs text-muted-foreground">Esta senha será usada para o login como jogador</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => onLink(playerId, password)} disabled={isLinking || !playerId || !password}>
            <UserCheck className="h-4 w-4 mr-2" /> {isLinking ? "Vinculando..." : "Vincular"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CreateFromPlayerDialog({ open, onOpenChange, players, onCreate, isCreating }) {
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("avaliador");
  const [searchQuery, setSearchQuery] = useState("");
  const [positionFilter, setPositionFilter] = useState("all");

  const handleOpenChange = (v) => {
    if (!v) { setSelectedPlayer(""); setEmail(""); setPassword(""); setSearchQuery(""); setPositionFilter("all"); }
    onOpenChange(v);
  };

  const filteredPlayers = players.filter(p => {
    const name = getPlayerName(p).toLowerCase();
    const matchesSearch = name.includes(searchQuery.toLowerCase());
    const matchesPosition = positionFilter === "all" || p.position === positionFilter;
    return matchesSearch && matchesPosition;
  });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-green-500" /> Criar Avaliador a partir de Jogador
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4 overflow-y-auto flex-1">
          <p className="text-sm text-muted-foreground">Selecione um jogador existente para transformá-lo em avaliador ou técnico.</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Buscar Jogador</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input type="text" placeholder="Digite o nome..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-input/50" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Filtrar por Posição</Label>
              <Select value={positionFilter} onValueChange={setPositionFilter}>
                <SelectTrigger className="bg-input/50"><SelectValue placeholder="Todas as posições" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as posições</SelectItem>
                  {POSITIONS.map(pos => (<SelectItem key={pos} value={pos}>{pos}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Selecione o Jogador ({filteredPlayers.length})</Label>
            <div className="max-h-48 overflow-y-auto border border-border/50 rounded-lg">
              {filteredPlayers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {searchQuery || positionFilter !== "all" ? "Nenhum jogador encontrado com esses filtros" : "Nenhum jogador cadastrado"}
                </p>
              ) : (
                filteredPlayers.map((player) => (
                  <div key={player.id}
                    className={`flex items-center gap-3 p-3 cursor-pointer transition-colors border-b border-border/30 last:border-b-0 ${
                      selectedPlayer === player.id ? 'bg-green-500/20 border-green-500/50' : 'hover:bg-secondary/50'
                    }`}
                    onClick={() => { setSelectedPlayer(player.id); if (player.email) setEmail(player.email); }}>
                    <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center overflow-hidden">
                      {player.photo_url ? (
                        <img src={getPhotoUrl(player.photo_url)} alt={getPlayerName(player)} className="w-full h-full object-cover" />
                      ) : (
                        <User className="h-5 w-5 text-slate-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{getPlayerName(player)}</p>
                      <p className="text-xs text-muted-foreground">{player.position}</p>
                    </div>
                    {selectedPlayer === player.id && <UserCheck className="h-5 w-5 text-green-500" />}
                  </div>
                ))
              )}
            </div>
          </div>

          {selectedPlayer && (
            <>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Email para Login</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input type="email" placeholder="email@exemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 bg-input/50" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 bg-input/50" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Nível de Acesso</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="bg-input/50"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="avaliador">Avaliador</SelectItem>
                    <SelectItem value="tecnico">Técnico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => onCreate(selectedPlayer, email, password, role)} disabled={isCreating || !selectedPlayer || !email || !password} className="bg-green-600 hover:bg-green-700">
            <UserPlus className="h-4 w-4 mr-2" /> {isCreating ? "Criando..." : "Criar Avaliador"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditEvaluatorDialog({ open, onOpenChange, evaluatorData, onSave, isSaving }) {
  const [name, setName] = useState(evaluatorData?.name || "");
  const [email, setEmail] = useState(evaluatorData?.email || "");
  const [password, setPassword] = useState("");

  // Sync with external data when dialog opens
  const handleOpenChange = (v) => {
    if (v && evaluatorData) {
      setName(evaluatorData.name || "");
      setEmail(evaluatorData.email || "");
      setPassword("");
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5 text-blue-500" /> Editar Avaliador
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Nome *</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input type="text" placeholder="Nome do avaliador" value={name} onChange={(e) => setName(e.target.value)} className="pl-10 bg-input/50" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Email *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input type="email" placeholder="email@exemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 bg-input/50" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Nova Senha (opcional)</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input type="password" placeholder="Deixe em branco para manter a atual" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 bg-input/50" />
            </div>
            <p className="text-xs text-muted-foreground">Preencha apenas se quiser alterar a senha</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => onSave(name, email, password)} disabled={isSaving || !name.trim() || !email.trim()} className="bg-blue-600 hover:bg-blue-700">
            <Save className="h-4 w-4 mr-2" /> {isSaving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
